import React, { useState, useEffect, useRef } from 'react';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import { FiCreditCard, FiCheck, FiCopy, FiRefreshCw, FiArrowRight, FiShield, FiStar } from 'react-icons/fi';
import { toast } from 'sonner';
import { formatBRL } from '../utils/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MP_PUBLIC_KEY = 'APP_USR-613ce1ac-4be9-42d1-aa07-f0ff1666d293';

export const Assinatura = () => {
  const { user, checkAuth } = useAuth();
  const [subInfo, setSubInfo] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState('essential');
  const [changingMethod, setChangingMethod] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [cardDocNumber, setCardDocNumber] = useState('');
  const [savingCard, setSavingCard] = useState(false);
  const [payingManual, setPayingManual] = useState(false);
  const [mpReady, setMpReady] = useState(false);
  const [payMonths, setPayMonths] = useState(1);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [payPixCode, setPayPixCode] = useState('');
  const [payPixQr, setPayPixQr] = useState('');
  const [payPixGenerated, setPayPixGenerated] = useState(false);
  const [payCopied, setPayCopied] = useState(false);
  const payAutoCheckRef = useRef(null);
  
  // Pricing states
  const [pricingInfo, setPricingInfo] = useState(null);
  
  // Plan Migration states
  const [migrationModalOpen, setMigrationModalOpen] = useState(false);
  const [migrationData, setMigrationData] = useState(null);
  const [migratingPlan, setMigratingPlan] = useState(false);

  useEffect(() => {
    loadSubscription();
    loadPricing();

    // Load MercadoPago.js
    if (!window.MercadoPago) {
      const script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.onload = () => setMpReady(true);
      document.head.appendChild(script);
    } else {
      setMpReady(true);
    }

    return () => {
      if (payAutoCheckRef.current) clearInterval(payAutoCheckRef.current);
    };
  }, []);

  const loadPricing = async () => {
    try {
      const res = await axios.get(`${API}/subscription-price`, { withCredentials: true });
      setPricingInfo(res.data);
    } catch (e) {
      console.error('Error loading subscription pricing:', e);
    }
  };

  const loadSubscription = async () => {
    setSubLoading(true);
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubInfo(response.data);
      const effectivePlan = response.data.plan_type || user?.plan_type;
      if (effectivePlan === 'basic' || effectivePlan === 'essential') {
        setSelectedPlan(effectivePlan);
      } else if (response.data.is_trial || response.data.payment_method === 'trial' || effectivePlan === 'trial') {
        setSelectedPlan('trial');
      } else if (effectivePlan) {
        setSelectedPlan(effectivePlan);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      toast.error('Erro ao carregar dados da assinatura');
    } finally {
      setSubLoading(false);
    }
  };

  const handlePlanChange = async (newPlan) => {
    const currentActivePlan = subInfo?.plan_type || user?.plan_type || 'basic';
    const isCurrentlyTrial = currentActivePlan !== 'basic' && currentActivePlan !== 'essential' && (subInfo?.is_trial || subInfo?.payment_method === 'trial' || currentActivePlan === 'trial');
    const hasActivePaidSub = !isCurrentlyTrial && subInfo?.status === 'active' && subInfo?.next_payment_date;

    if (hasActivePaidSub && newPlan !== currentActivePlan && newPlan !== 'trial') {
      // User has active paid days and wants to switch -> Preview proportional migration
      try {
        const previewRes = await axios.post(
          `${API}/subscription/preview-migration`,
          { target_plan: newPlan },
          { withCredentials: true }
        );
        setMigrationData(previewRes.data);
        setMigrationModalOpen(true);
      } catch (err) {
        console.error('Migration preview error:', err);
        toast.error('Não foi possível calcular a conversão de dias.');
      }
    } else {
      // Just change selected plan for payment/renewal
      setSelectedPlan(newPlan);
    }
  };

  const handleConfirmMigration = async () => {
    if (!migrationData?.target_plan) return;
    setMigratingPlan(true);
    try {
      const res = await axios.post(
        `${API}/subscription/migrate-plan`,
        { target_plan: migrationData.target_plan },
        { withCredentials: true }
      );
      toast.success(res.data.message || 'Plano migrado com sucesso!');
      setSelectedPlan(migrationData.target_plan);
      setMigrationModalOpen(false);
      setMigrationData(null);
      await loadSubscription();
      await checkAuth();
    } catch (error) {
      console.error('Migration error:', error);
      const detail = error?.response?.data?.detail || 'Erro ao migrar plano';
      toast.error(detail);
    } finally {
      setMigratingPlan(false);
    }
  };

  const toggleAutoRenew = async (checked) => {
    try {
      await axios.put(
        `${API}/subscription/settings`,
        { auto_renew: checked },
        { withCredentials: true }
      );
      setSubInfo(prev => ({ ...prev, auto_renew: checked }));
      toast.success(checked ? 'Cobrança automática ativada!' : 'Cobrança automática desativada');
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Erro ao atualizar configuração');
    }
  };

  const formatCardNum = (v) => {
    const c = v.replace(/\D/g, '').slice(0, 16);
    return c.replace(/(\d{4})/g, '$1 ').trim();
  };

  const formatExp = (v) => {
    const c = v.replace(/\D/g, '').slice(0, 4);
    if (c.length >= 3) return c.slice(0, 2) + '/' + c.slice(2);
    return c;
  };

  const handleChangePaymentMethod = async () => {
    if (!newPaymentMethod) return;

    if (newPaymentMethod === 'pix') {
      try {
        await axios.put(`${API}/subscription/settings`, { payment_method: 'pix' }, { withCredentials: true });
        setChangingMethod(false);
        setNewPaymentMethod('');
        toast.success('Forma de pagamento alterada para PIX!');
        loadSubscription();
      } catch (error) {
        toast.error('Erro ao alterar forma de pagamento');
      }
      return;
    }

    if (!cardNumber || !cardExpiry || !cardCvv || !cardHolder || !cardDocNumber) {
      toast.error('Preencha todos os dados do cartão');
      return;
    }
    if (!mpReady || !window.MercadoPago) {
      toast.error('Aguarde o carregamento do sistema de pagamento');
      return;
    }

    setSavingCard(true);
    try {
      const mp = new window.MercadoPago(MP_PUBLIC_KEY);
      const [expMonth, expYear] = cardExpiry.split('/');

      let tokenResponse;
      try {
        tokenResponse = await mp.createCardToken({
          cardNumber: cardNumber.replace(/\s/g, ''),
          cardholderName: cardHolder,
          cardExpirationMonth: expMonth.trim(),
          cardExpirationYear: expYear.trim().length === 2 ? '20' + expYear.trim() : expYear.trim(),
          securityCode: cardCvv,
          identificationType: 'CPF',
          identificationNumber: cardDocNumber.replace(/\D/g, '')
        });
      } catch (tokenError) {
        console.error('Token error:', tokenError);
        const errorMsg = tokenError?.message || tokenError?.cause?.[0]?.description || String(tokenError);
        let friendlyMsg = 'Erro ao processar dados do cartão.';

        if (errorMsg.includes('card_number') || errorMsg.includes('cardNumber') || errorMsg.includes('invalid')) {
          friendlyMsg = 'Número do cartão inválido. Verifique os dígitos e tente novamente.';
        } else if (errorMsg.includes('expiration') || errorMsg.includes('expired')) {
          friendlyMsg = 'Data de validade inválida ou cartão expirado.';
        } else if (errorMsg.includes('security_code') || errorMsg.includes('securityCode') || errorMsg.includes('cvv')) {
          friendlyMsg = 'Código CVV inválido. Verifique o código de segurança do cartão.';
        } else if (errorMsg.includes('identification') || errorMsg.includes('document')) {
          friendlyMsg = 'CPF inválido. Verifique o número do documento.';
        } else if (errorMsg.includes('cardholder')) {
          friendlyMsg = 'Nome do titular inválido.';
        }

        toast.error(friendlyMsg);
        setSavingCard(false);
        return;
      }

      if (!tokenResponse || !tokenResponse.id) {
        toast.error('Não foi possível validar o cartão. Verifique todos os dados e tente novamente.');
        setSavingCard(false);
        return;
      }

      await axios.put(
        `${API}/subscription/settings`,
        { 
          payment_method: newPaymentMethod, 
          card_token: tokenResponse.id,
          card_last_four: cardNumber.replace(/\s/g, '').slice(-4),
          card_brand: newPaymentMethod
        },
        { withCredentials: true }
      );

      setSubInfo(prev => ({
        ...prev,
        payment_method: newPaymentMethod,
        card_last_four: cardNumber.replace(/\s/g, '').slice(-4),
        card_brand: newPaymentMethod
      }));
      setChangingMethod(false);
      setNewPaymentMethod('');
      setCardNumber(''); setCardExpiry(''); setCardCvv(''); setCardHolder(''); setCardDocNumber('');
      toast.success('Cartão salvo com sucesso!');
      loadSubscription();
    } catch (error) {
      console.error('Error saving card:', error);
      const detail = error?.response?.data?.detail || '';
      toast.error(detail ? `Erro ao salvar cartão: ${detail}` : 'Erro ao salvar cartão.');
    } finally {
      setSavingCard(false);
    }
  };

  const handleManualPayment = async () => {
    const method = subInfo?.payment_method || 'pix';
    setPayingManual(true);

    if (method === 'pix') {
      try {
        const response = await axios.post(
          `${API}/subscription/create`,
          { payment_method: 'pix', auto_renew: subInfo?.auto_renew || false, months: payMonths, plan_type: selectedPlan },
          { withCredentials: true }
        );
        setPayPixCode(response.data.pix_qr_code || '');
        setPayPixQr(response.data.pix_qr_code_base64 || '');
        setPayPixGenerated(true);

        payAutoCheckRef.current = setInterval(async () => {
          try {
            const check = await axios.post(`${API}/subscription/check-payment`, {}, { withCredentials: true });
            if (check.data.status === 'active') {
              if (payAutoCheckRef.current) clearInterval(payAutoCheckRef.current);
              payAutoCheckRef.current = null;
              toast.success(`Pagamento confirmado! ${payMonths} mês(es) adicionado(s).`);
              setShowPayDialog(false);
              setPayPixGenerated(false);
              setPayMonths(1);
              await loadSubscription();
              await checkAuth();
            }
          } catch (e) { /* silent */ }
        }, 10000);
      } catch (error) {
        console.error('Error generating PIX:', error);
        toast.error('Erro ao gerar PIX');
      } finally {
        setPayingManual(false);
      }
      return;
    }

    try {
      const response = await axios.post(
        `${API}/subscription/create`,
        { payment_method: method, auto_renew: subInfo?.auto_renew || false, use_stored_card: true, months: payMonths, plan_type: selectedPlan },
        { withCredentials: true }
      );
      if (response.data.status === 'active') {
        toast.success(`Pagamento aprovado! ${payMonths} mês(es) adicionado(s).`);
        handleClosePayDialog();
        await loadSubscription();
        await checkAuth();
      } else {
        toast.error('Pagamento não aprovado. Atualize os dados do cartão em "Alterar Forma de Pagamento".');
      }
    } catch (error) {
      console.error('Error card payment:', error);
      const detail = error?.response?.data?.detail || '';
      if (detail.includes('Token') || detail.includes('token') || detail.includes('obrigatório')) {
        toast.error('Dados do cartão não encontrados. Atualize em "Alterar Forma de Pagamento" primeiro.');
      } else {
        toast.error(detail || 'Erro ao processar pagamento com cartão');
      }
    } finally {
      setPayingManual(false);
    }
  };

  const handleCopyPayPix = () => {
    navigator.clipboard.writeText(payPixCode);
    setPayCopied(true);
    toast.success('Código PIX copiado!');
    setTimeout(() => setPayCopied(false), 3000);
  };

  const handleClosePayDialog = () => {
    if (payAutoCheckRef.current) clearInterval(payAutoCheckRef.current);
    payAutoCheckRef.current = null;
    setShowPayDialog(false);
    setPayPixGenerated(false);
    setPayPixCode('');
    setPayPixQr('');
    setPayMonths(1);
  };

  if (subLoading) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
            <p className="mt-4 text-blue-900 font-medium">Carregando assinatura...</p>
          </div>
        </div>
      </div>
    );
  }

  const activePlan = subInfo?.plan_type || user?.plan_type;
  const isTrial = Boolean(
    activePlan !== 'basic' &&
    activePlan !== 'essential' &&
    (
      subInfo?.is_trial ||
      subInfo?.payment_method === 'trial' ||
      subInfo?.plan_type === 'trial' ||
      user?.trial_period ||
      user?.plan_type === 'trial'
    )
  );

  // Calculate prices based on selected plan
  const isTrialSelected = selectedPlan === 'trial';
  const planOriginalPrice = isTrialSelected
    ? 0.0
    : selectedPlan === 'basic'
      ? (pricingInfo?.basic_price ?? 10.0)
      : (pricingInfo?.essential_price ?? 37.90);

  const planFinalPrice = isTrialSelected
    ? 0.0
    : selectedPlan === 'basic'
      ? (pricingInfo?.basic_final_price ?? planOriginalPrice)
      : (pricingInfo?.essential_final_price ?? planOriginalPrice);

  const hasBasicDiscount = Boolean(pricingInfo?.has_basic_discount || ((pricingInfo?.basic_final_price ?? 10.0) < (pricingInfo?.basic_price ?? 10.0)));
  const hasEssentialDiscount = Boolean(pricingInfo?.has_essential_discount || ((pricingInfo?.essential_final_price ?? 37.90) < (pricingInfo?.essential_price ?? 37.90)));
  const hasDiscount = Boolean(!isTrialSelected && (pricingInfo?.has_discount ?? (planFinalPrice < planOriginalPrice)));
  const discountType = pricingInfo?.discount_type;
  const discountValue = pricingInfo?.discount_value ?? 0;

  const getDiscountTag = (val, type) => {
    const v = val ?? discountValue;
    const t = type ?? discountType;
    if (t === 'percentage') {
      return `${Math.round(v)}% OFF`;
    }
    return `${formatBRL(v)} OFF`;
  };

  return (
    <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
      <Header />
      
      <main className="transition-all duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-black text-blue-900 dark:text-white mb-1">Assinatura</h1>
          <p className="text-slate-600 dark:text-slate-400">Gerencie seu plano, tipo de assinatura, forma de pagamento e renovação</p>
        </div>

        <Card data-testid="subscription-management-card" className="bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-white/10 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl font-semibold text-blue-900 dark:text-white">
              <FiCreditCard size={24} />
              Status do Plano
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {subInfo && subInfo.status !== 'none' ? (
              <>
                {/* LINHA 1: Status | Tipo de Assinatura | Dias Restantes */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* 1. Status */}
                  <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-4 border border-slate-200/60 dark:border-white/5">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 font-semibold uppercase tracking-wider">Status</p>
                    <p className={`text-xl font-bold ${subInfo.status === 'active' ? 'text-green-600 dark:text-green-400' : subInfo.status === 'pending' ? 'text-amber-500' : 'text-red-600'}`}>
                      {subInfo.status === 'active' ? 'Ativa' : subInfo.status === 'pending' ? 'Pendente' : 'Expirada'}
                    </p>
                    {isTrial && (
                      <span className="inline-block mt-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full border border-emerald-300 dark:border-emerald-800">
                        Período de Teste
                      </span>
                    )}
                  </div>

                  {/* 2. Tipo de Assinatura (Select Básico / Essencial / Período de Teste) */}
                  <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-4 border border-slate-200/60 dark:border-white/5">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 font-semibold uppercase tracking-wider">Tipo de Assinatura</p>
                    <Select value={selectedPlan} onValueChange={handlePlanChange}>
                      <SelectTrigger className="w-full bg-white dark:bg-slate-900 border-blue-900/20 dark:border-white/10 font-bold text-blue-900 dark:text-white mt-1 shadow-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {isTrial && (
                          <SelectItem value="trial" className="font-semibold text-emerald-600 dark:text-emerald-400">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                              Período de Teste (Ativo - 10 dias)
                            </span>
                          </SelectItem>
                        )}
                        <SelectItem value="basic" className="font-medium">
                          <span className="flex items-center justify-between gap-3 w-full">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                              Básico ({formatBRL(pricingInfo?.basic_final_price ?? 10.0)}/mês)
                            </span>
                            {hasBasicDiscount && (
                              <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-md bg-emerald-500 text-white leading-none shadow-xs">
                                {getDiscountTag(pricingInfo?.discount_value, pricingInfo?.discount_type)}
                              </span>
                            )}
                          </span>
                        </SelectItem>
                        <SelectItem value="essential" className="font-semibold">
                          <span className="flex items-center justify-between gap-3 w-full">
                            <span className="flex items-center gap-2 text-cyan-600 dark:text-cyan-400">
                              <FiStar size={14} className="text-amber-400 fill-amber-400 flex-shrink-0" />
                              Essencial ({formatBRL(pricingInfo?.essential_final_price ?? 37.90)}/mês)
                            </span>
                            {hasEssentialDiscount && (
                              <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-md bg-emerald-500 text-white leading-none shadow-xs">
                                {getDiscountTag(pricingInfo?.discount_value, pricingInfo?.discount_type)}
                              </span>
                            )}
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* 3. Dias Restantes */}
                  <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-4 border border-slate-200/60 dark:border-white/5">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 font-semibold uppercase tracking-wider">Dias Restantes</p>
                    {(() => {
                      if (!subInfo.next_payment_date || subInfo.status !== 'active') {
                        return <p className="text-xl font-bold text-red-600">0 dias</p>;
                      }
                      const now = new Date();
                      const expiry = new Date(subInfo.next_payment_date);
                      const diffMs = expiry - now;
                      const days = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
                      const color = days <= 7 ? 'text-red-600' : days <= 15 ? 'text-yellow-600' : 'text-green-600 dark:text-green-400';
                      return <p className={`text-xl font-bold ${color}`} data-testid="days-remaining">{days} dia(s)</p>;
                    })()}
                  </div>
                </div>

                {/* LINHA 2: Forma de Pagamento | Vencimento da Assinatura */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Card 1: Forma de Pagamento */}
                  <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-4 border border-slate-200/60 dark:border-white/5 flex flex-col justify-between">
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 font-semibold uppercase tracking-wider">Forma de Pagamento</p>
                      <p className="text-lg font-bold text-blue-900 dark:text-white">
                        {(subInfo.payment_method === 'pix' || !subInfo.payment_method) && 'PIX'}
                        {subInfo.payment_method === 'credit_card' && `Cartão de Crédito ${subInfo.card_last_four ? `(**** ${subInfo.card_last_four})` : ''}`}
                        {subInfo.payment_method === 'debit_card' && `Cartão de Débito ${subInfo.card_last_four ? `(**** ${subInfo.card_last_four})` : ''}`}
                        {subInfo.payment_method === 'trial' && '10 Dias Grátis'}
                        {subInfo.payment_method === 'free' && 'Gratuito'}
                        {subInfo.payment_method === 'manual' && 'Manual'}
                      </p>
                    </div>

                    {!changingMethod && (
                      <div className="mt-3 pt-2 border-t border-slate-200/60 dark:border-white/5">
                        <Button
                          data-testid="change-payment-method-btn"
                          variant="outline"
                          size="sm"
                          onClick={() => { setChangingMethod(true); setNewPaymentMethod(subInfo.payment_method || 'pix'); }}
                          className="w-full sm:w-auto border-blue-900/30 text-blue-900 dark:text-cyan-300 dark:border-cyan-500/30 hover:bg-blue-50 dark:hover:bg-cyan-950/30 text-xs font-semibold"
                        >
                          Alterar Forma de Pagamento
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Card 2: Vencimento da Assinatura */}
                  <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-4 border border-slate-200/60 dark:border-white/5 flex flex-col justify-between">
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 font-semibold uppercase tracking-wider">Vencimento da Assinatura</p>
                      <p className="text-base font-bold text-blue-900 dark:text-white">
                        {subInfo.next_payment_date
                          ? new Date(subInfo.next_payment_date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
                          : 'Não definido'}
                      </p>
                    </div>

                    <div className="mt-3 pt-2 border-t border-slate-200/60 dark:border-white/5 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                          Valor do Plano ({selectedPlan === 'trial' ? 'Período de Teste' : selectedPlan === 'basic' ? 'Básico' : 'Essencial'})
                        </span>
                        {selectedPlan === 'trial' ? (
                          <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                            Grátis (10 dias)
                          </span>
                        ) : hasDiscount && discountValue > 0 ? (
                          <div className="flex items-center gap-1.5" data-testid="discounted-price-container">
                            <span className="text-xs text-slate-400 line-through">
                              {formatBRL(planOriginalPrice)}
                            </span>
                            <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                              {formatBRL(planFinalPrice)}/mês
                            </span>
                            <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-md bg-emerald-500 text-white leading-none shadow-xs">
                              {getDiscountTag(discountValue, discountType)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm font-bold text-blue-900 dark:text-white">
                            {formatBRL(planFinalPrice)}/mês
                          </span>
                        )}
                      </div>

                      {/* Próxima renovação com desconto (caso esteja em Período de Teste) */}
                      {selectedPlan === 'trial' && (hasEssentialDiscount || hasBasicDiscount) && (
                        <div className="flex items-center justify-between text-xs pt-1.5 border-t border-dashed border-slate-200 dark:border-white/10">
                          <span className="text-slate-500 dark:text-slate-400 font-medium">
                            Próxima renovação:
                          </span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] text-slate-400 line-through">
                              {formatBRL(pricingInfo?.essential_price ?? 37.90)}
                            </span>
                            <span className="font-bold text-emerald-600 dark:text-emerald-400">
                              Essencial ({formatBRL(pricingInfo?.essential_final_price ?? 3.79)}/mês)
                            </span>
                            <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-md bg-emerald-500 text-white leading-none shadow-xs">
                              {getDiscountTag(pricingInfo?.discount_value, pricingInfo?.discount_type)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Formulário de Alteração de Cartão / PIX */}
                {changingMethod && (
                  <div className="border border-blue-900/20 dark:border-cyan-500/30 bg-slate-50 dark:bg-slate-800/80 rounded-xl p-5 space-y-4 animate-in fade-in duration-200">
                    <div>
                      <Label className="text-blue-900 dark:text-white font-semibold">Nova Forma de Pagamento</Label>
                      <Select value={newPaymentMethod} onValueChange={setNewPaymentMethod}>
                        <SelectTrigger data-testid="new-payment-method-select" className="mt-1 bg-white dark:bg-slate-900">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pix">PIX</SelectItem>
                          <SelectItem value="credit_card">Cartão de Crédito</SelectItem>
                          <SelectItem value="debit_card">Cartão de Débito</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Card form */}
                    {newPaymentMethod !== 'pix' && (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-blue-900 dark:text-white font-semibold text-sm">Número do Cartão</Label>
                          <Input
                            data-testid="profile-card-number"
                            value={cardNumber}
                            onChange={(e) => setCardNumber(formatCardNum(e.target.value))}
                            placeholder="0000 0000 0000 0000"
                            maxLength={19}
                            className="mt-1 bg-white dark:bg-slate-900"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-blue-900 dark:text-white font-semibold text-sm">Validade</Label>
                            <Input
                              data-testid="profile-card-expiry"
                              value={cardExpiry}
                              onChange={(e) => setCardExpiry(formatExp(e.target.value))}
                              placeholder="MM/AA"
                              maxLength={5}
                              className="mt-1 bg-white dark:bg-slate-900"
                            />
                          </div>
                          <div>
                            <Label className="text-blue-900 dark:text-white font-semibold text-sm">CVV</Label>
                            <Input
                              data-testid="profile-card-cvv"
                              value={cardCvv}
                              onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                              placeholder="123"
                              maxLength={4}
                              type="password"
                              className="mt-1 bg-white dark:bg-slate-900"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-blue-900 dark:text-white font-semibold text-sm">Nome no Cartão</Label>
                          <Input
                            data-testid="profile-card-holder"
                            value={cardHolder}
                            onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
                            placeholder="NOME COMO NO CARTÃO"
                            className="mt-1 bg-white dark:bg-slate-900"
                          />
                        </div>
                        <div>
                          <Label className="text-blue-900 dark:text-white font-semibold text-sm">CPF do Titular</Label>
                          <Input
                            data-testid="profile-card-doc"
                            value={cardDocNumber}
                            onChange={(e) => setCardDocNumber(e.target.value)}
                            placeholder="000.000.000-00"
                            className="mt-1 bg-white dark:bg-slate-900"
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setChangingMethod(false); setNewPaymentMethod(''); }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        data-testid="save-payment-method-btn"
                        size="sm"
                        onClick={handleChangePaymentMethod}
                        disabled={savingCard}
                        className="bg-blue-900 hover:bg-blue-800 text-white"
                      >
                        {savingCard ? 'Salvando...' : 'Salvar'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Auto Renew Toggle */}
                <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl p-4 border border-slate-200/60 dark:border-white/5">
                  <Checkbox
                    id="auto-renew-profile"
                    data-testid="auto-renew-toggle"
                    checked={subInfo.auto_renew || false}
                    onCheckedChange={toggleAutoRenew}
                  />
                  <Label htmlFor="auto-renew-profile" className="cursor-pointer">
                    <span className="font-semibold text-blue-900 dark:text-white">Cobrança automática</span>
                    <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {(() => {
                        const activeMethod = changingMethod ? newPaymentMethod : (subInfo.payment_method || 'pix');
                        return activeMethod === 'pix'
                          ? 'Você receberá um lembrete por email 1 dia antes do vencimento da sua assinatura.'
                          : 'A renovação será cobrada automaticamente no seu cartão 1 dia antes do vencimento da assinatura.';
                      })()}
                    </span>
                  </Label>
                </div>

                {/* Manual Payment / Renewal Button / Trial Card */}
                {selectedPlan === 'trial' ? (
                  <div className="border border-emerald-500/30 bg-gradient-to-br from-emerald-50/40 via-blue-50/20 to-emerald-50/40 dark:from-emerald-950/20 dark:via-blue-950/10 dark:to-emerald-950/20 rounded-xl p-5 text-center space-y-3 mt-4 shadow-sm">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 font-bold text-xs border border-emerald-300 dark:border-emerald-700/60 shadow-sm">
                      <FiStar size={14} className="text-amber-500 fill-amber-400" />
                      Período de Teste Ativo (Acesso Total Liberado)
                    </div>
                    <p className="text-sm font-semibold text-blue-900 dark:text-white">
                      Você está aproveitando o Período de Teste de 10 dias gratuitos.
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400 max-w-lg mx-auto leading-relaxed">
                      Todos os recursos da plataforma estão liberados para você. Para garantir a continuidade do seu acesso sem interrupções após os 10 dias, selecione o <strong className="text-blue-900 dark:text-cyan-300">Plano Básico</strong> ou <strong className="text-blue-900 dark:text-cyan-300">Plano Essencial</strong> no campo <em>Tipo de Assinatura</em> acima.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handlePlanChange('basic')}
                        className="border-slate-300 dark:border-white/10 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-bold gap-2"
                      >
                        <span>Contratar Básico ({formatBRL(pricingInfo?.basic_final_price ?? 10.0)}/mês)</span>
                        {hasBasicDiscount && (
                          <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-600 text-white leading-none shadow-xs">
                            {getDiscountTag(pricingInfo?.discount_value, pricingInfo?.discount_type)}
                          </span>
                        )}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handlePlanChange('essential')}
                        className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-bold shadow-md shadow-blue-900/20 gap-2"
                      >
                        <span>⭐ Contratar Essencial ({formatBRL(pricingInfo?.essential_final_price ?? 37.90)}/mês)</span>
                        {hasEssentialDiscount && (
                          <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-500 text-slate-950 font-black leading-none shadow-xs">
                            {getDiscountTag(pricingInfo?.discount_value, pricingInfo?.discount_type)}
                          </span>
                        )}
                      </Button>
                    </div>
                    {(hasEssentialDiscount || hasBasicDiscount) && (
                      <p className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 pt-1">
                        🎉 Desconto especial concedido: Você possui {getDiscountTag(pricingInfo?.discount_value, pricingInfo?.discount_type)} de desconto garantido na sua conta!
                      </p>
                    )}
                  </div>
                ) : (() => {
                  const isAutoCard = subInfo.auto_renew && subInfo.payment_method && subInfo.payment_method !== 'pix';
                  return (
                    <div className="pt-2">
                      {!showPayDialog ? (
                        <Button
                          data-testid="manual-pay-button"
                          onClick={() => { setShowPayDialog(true); setPayMonths(1); }}
                          disabled={isAutoCard}
                          className={`w-full py-6 text-base font-bold flex items-center justify-center gap-2 ${
                            isAutoCard
                              ? 'bg-slate-300 text-slate-500 cursor-not-allowed hover:bg-slate-300'
                              : 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20'
                          }`}
                        >
                          <FiCreditCard size={20} />
                          <span>Pagar assinatura ({selectedPlan === 'basic' ? 'Básico' : 'Essencial'}) - {formatBRL(planFinalPrice)}/mês</span>
                          {hasDiscount && discountValue > 0 && (
                            <span className="ml-1 text-[11px] font-black uppercase px-2 py-0.5 rounded-full bg-white text-green-700 leading-none shadow-xs">
                              {getDiscountTag(discountValue, discountType)}
                            </span>
                          )}
                        </Button>
                      ) : (
                        <div className="border border-green-600/30 bg-green-50/20 dark:bg-green-950/10 rounded-xl p-5 space-y-4">
                          {/* PIX QR Code Display */}
                          {payPixGenerated && (subInfo?.payment_method === 'pix' || !subInfo?.payment_method) ? (
                            <>
                              <p className="text-sm font-semibold text-blue-900 dark:text-white text-center">Escaneie o QR Code ou copie o código PIX</p>
                              {payPixQr && (
                                <div className="flex justify-center">
                                  <div className="bg-white border-2 border-blue-900/20 rounded-xl p-3 shadow-md">
                                    <img src={`data:image/png;base64,${payPixQr}`} alt="QR Code PIX" className="w-48 h-48" data-testid="pay-pix-qr" />
                                  </div>
                                </div>
                              )}
                              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 border border-slate-200 dark:border-white/10">
                                <p className="text-xs font-bold text-blue-900/70 dark:text-cyan-300 mb-1">PIX COPIA E COLA</p>
                                <div className="break-all font-mono text-xs text-slate-600 dark:text-slate-300 max-h-16 overflow-y-auto mb-2">{payPixCode}</div>
                                <Button
                                  data-testid="pay-copy-pix"
                                  onClick={handleCopyPayPix}
                                  size="sm"
                                  className="w-full bg-blue-900 hover:bg-blue-800 text-white flex items-center justify-center gap-2 font-bold"
                                >
                                  {payCopied ? <><FiCheck size={14} /> Copiado!</> : <><FiCopy size={14} /> Copiar Código PIX</>}
                                </Button>
                              </div>
                              <p className="text-xs text-slate-500 text-center">Verificação automática a cada 10 segundos</p>
                              <div className="bg-slate-50 dark:bg-slate-900/60 rounded-lg p-4 text-center border border-slate-200 dark:border-white/10">
                                <p className="text-xs text-slate-600 dark:text-slate-400">Total pago</p>
                                <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
                                  {hasDiscount && discountValue > 0 && (
                                    <span className="text-sm text-slate-400 line-through font-medium">
                                      {formatBRL(payMonths * planOriginalPrice)}
                                    </span>
                                  )}
                                  <p className="text-xl font-black text-blue-900 dark:text-white">{formatBRL(payMonths * planFinalPrice)}</p>
                                  {hasDiscount && discountValue > 0 && (
                                    <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-md bg-emerald-500 text-white leading-none shadow-xs">
                                      {getDiscountTag(discountValue, discountType)}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500 mt-1">{payMonths} x {formatBRL(planFinalPrice)} = +{payMonths * 30} dias ({selectedPlan === 'basic' ? 'Básico' : 'Essencial'})</p>
                              </div>
                              <Button variant="outline" onClick={handleClosePayDialog} className="w-full">Fechar</Button>
                            </>
                          ) : (
                            <>
                              {/* Month Selector */}
                              <div className="bg-slate-50 dark:bg-slate-900/60 rounded-lg p-4 space-y-3 border border-slate-200 dark:border-white/10">
                                <p className="text-sm font-semibold text-blue-900 dark:text-white">Quantos meses deseja pagar?</p>
                                <div className="flex items-center gap-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPayMonths(Math.max(1, payMonths - 1))}
                                    disabled={payMonths <= 1}
                                    className="w-10 h-10 p-0 text-lg font-bold"
                                  >
                                    -
                                  </Button>
                                  <div className="flex-1 text-center">
                                    <p className="text-3xl font-black text-blue-900 dark:text-white" data-testid="pay-months-value">{payMonths}</p>
                                    <p className="text-xs text-slate-500">{payMonths === 1 ? 'mês' : 'meses'}</p>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPayMonths(Math.min(12, payMonths + 1))}
                                    disabled={payMonths >= 12}
                                    className="w-10 h-10 p-0 text-lg font-bold"
                                  >
                                    +
                                  </Button>
                                </div>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-900/60 rounded-lg p-4 text-center border border-slate-200 dark:border-white/10">
                                <p className="text-xs text-slate-600 dark:text-slate-400">Total a pagar ({selectedPlan === 'basic' ? 'Plano Básico' : 'Plano Essencial'})</p>
                                <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
                                  {hasDiscount && discountValue > 0 && (
                                    <span className="text-sm text-slate-400 line-through font-medium">
                                      {formatBRL(payMonths * planOriginalPrice)}
                                    </span>
                                  )}
                                  <p className="text-2xl font-black text-blue-900 dark:text-white" data-testid="pay-total">
                                    {formatBRL(payMonths * planFinalPrice)}
                                  </p>
                                  {hasDiscount && discountValue > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-black bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700/60">
                                      {discountType === 'percentage' ? `${discountValue}% OFF` : `-${formatBRL(payMonths * discountValue)}`}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500 mt-1">{payMonths} x {formatBRL(planFinalPrice)} = +{payMonths * 30} dias</p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  onClick={handleClosePayDialog}
                                  className="flex-1"
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  data-testid="confirm-pay-button"
                                  onClick={handleManualPayment}
                                  disabled={payingManual}
                                  className="flex-1 bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2 font-bold"
                                >
                                  {payingManual ? (
                                    <><FiRefreshCw size={14} className="animate-spin" /> Processando...</>
                                  ) : (
                                    <>Pagar {formatBRL(payMonths * planFinalPrice)}</>
                                  )}
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {isAutoCard && !showPayDialog && (
                        <p className="text-xs text-slate-500 text-center mt-2">
                          A cobrança automática está ativa no cartão. O pagamento será feito automaticamente.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>Nenhuma assinatura ativa encontrada.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* MODAL DE MIGRAÇÃO DE PLANO COM CONVERSÃO DE DIAS */}
      <Dialog open={migrationModalOpen} onOpenChange={setMigrationModalOpen}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900 dark:text-white flex items-center gap-2">
              <FiRefreshCw className="text-cyan-600" />
              Migração de Assinatura
            </DialogTitle>
          </DialogHeader>

          {migrationData && (
            <div className="space-y-4 py-3">
              <div className="bg-blue-50/50 dark:bg-blue-950/30 rounded-xl p-4 border border-blue-100 dark:border-blue-900/40">
                <div className="flex items-center justify-between text-sm">
                  <div className="text-center flex-1">
                    <p className="text-xs text-slate-500 font-semibold uppercase">Plano Atual</p>
                    <p className="text-base font-bold text-slate-800 dark:text-white capitalize">
                      {migrationData.current_plan === 'basic' ? 'Básico' : 'Essencial'}
                    </p>
                    <p className="text-xs text-slate-500">{migrationData.current_days} dia(s)</p>
                  </div>

                  <div className="px-3 text-cyan-600">
                    <FiArrowRight size={20} />
                  </div>

                  <div className="text-center flex-1">
                    <p className="text-xs text-cyan-600 dark:text-cyan-400 font-semibold uppercase">Novo Plano</p>
                    <p className="text-base font-bold text-blue-900 dark:text-cyan-300 capitalize">
                      {migrationData.target_plan === 'basic' ? 'Básico' : 'Essencial'}
                    </p>
                    <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{migrationData.converted_days} dia(s)</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-3 text-xs text-slate-600 dark:text-slate-300 space-y-1.5 border border-slate-200/50 dark:border-white/5">
                <p className="font-semibold text-slate-900 dark:text-white">Conversão Proporcional de Dias:</p>
                <p>
                  Seus <strong>{migrationData.current_days} dia(s)</strong> no plano {migrationData.current_plan === 'basic' ? 'Básico' : 'Essencial'} serão convertidos em <strong>{migrationData.converted_days} dia(s)</strong> no plano {migrationData.target_plan === 'basic' ? 'Básico' : 'Essencial'} com base no valor de cada plano, sem custo adicional agora.
                </p>
                {migrationData.target_plan === 'essential' && (
                  <p className="text-emerald-600 dark:text-emerald-400 font-medium">
                    ✓ Liberação imediata de Conciliação Bancária, Carteiras e Juros.
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setMigrationModalOpen(false);
                setMigrationData(null);
              }}
              disabled={migratingPlan}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmMigration}
              disabled={migratingPlan}
              className="bg-blue-900 hover:bg-blue-800 text-white font-bold"
            >
              {migratingPlan ? <><FiRefreshCw className="animate-spin mr-1.5" size={14} /> Migrando...</> : 'Confirmar Migração'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </main>
    </div>
  );
};
